import { api } from "../../../api/api";
import { COUNTRIES } from "../../../constants/countries";
import { getCountryCode, replaceCountryCode } from "../../../helpers/replaceConutryCode";
import { IActionItem } from "../../../redux/actionItems";
import { ILead, initialLead, initialMultipleEvent } from "../../../redux/leads/leads.types";
import { resolveExtLeadEventFields } from "./resolveExtLeadEventFields";

interface IDriveStatus {
    status_uuid: string;
    name: string;
    actual_status: string;
    status: string;
}

interface IDriveExternalStatus {
    external_status_uuid: string;
    name: string;
    actual_status: string;
    status: string;
}

export interface IConvertExtLeadResult {
    event_lead: ILead;
    ext_lead: IActionItem;
}

const fetchLeadDriveExternalStatus = async (
    companyOutletUuid: string
): Promise<IDriveExternalStatus | null> => {
    try {
        const res = await api.get(
            `/dataManagement/get-drive-external-status?company_outlet_uuid=${companyOutletUuid}&actual_status=LEAD`
        );
        const list: IDriveExternalStatus[] = res.data.data || [];
        return list[0] || null;
    } catch {
        return null;
    }
};

const fetchLeadDriveStatus = async (
    companyOutletUuid: string
): Promise<IDriveStatus | null> => {
    try {
        const res = await api.get(
            `/dataManagement/get-drive-status?company_outlet_uuid=${companyOutletUuid}&status=ACTIVE&actual_status=ONGOING`
        );
        const list: IDriveStatus[] = res.data.data || [];
        return list[0] || null;
    } catch {
        return null;
    }
};

const fetchLeadSourceUuid = async (
    companyOutletUuid: string,
    sourceName: string
): Promise<string | null> => {
    try {
        const res = await api.get(
            `/leads/get-lead-source?company_outlet_uuid=${companyOutletUuid}&status=ACTIVE`
        );
        const list = res.data.data || [];
        const sourceLower = sourceName.toLowerCase().trim();
        const matched =
            list.find((s: any) => s.source_name?.toLowerCase().trim() === sourceLower) ||
            list.find(
                (s: any) =>
                    s.source_name?.toLowerCase().includes(sourceLower) ||
                    sourceLower.includes(s.source_name?.toLowerCase())
            );
        return matched?.lead_source_uuid || null;
    } catch {
        return null;
    }
};

export const convertExtLeadToEvtLead = async (
    actionItem: IActionItem,
    companyOutletUuid: string
): Promise<IConvertExtLeadResult> => {
    const [externalStatus, driveStatus, resolvedLeadSourceUuid] = await Promise.all([
        fetchLeadDriveExternalStatus(companyOutletUuid),
        fetchLeadDriveStatus(companyOutletUuid),
        actionItem.lead_source_uuid
            ? Promise.resolve(actionItem.lead_source_uuid)
            : actionItem.source
                ? fetchLeadSourceUuid(companyOutletUuid, actionItem.source)
                : Promise.resolve(null),
    ]);

    const externalStatusName = externalStatus?.name || "LEAD";
    const externalActualStatus = externalStatus?.actual_status || "LEAD";
    const externalStatusUuid = externalStatus?.external_status_uuid || "";

    const driveStatusName = driveStatus?.name || "ONGOING";
    const driveActualStatusName = driveStatus?.actual_status || "ONGOING";
    const driveStatusUuid = driveStatus?.status_uuid || "";

    const resolvedCountryCode =
        actionItem.country_code ||
        getCountryCode(actionItem.mobile_no) ||
        "+91";
    const resolvedCountryIsoCode =
        actionItem.country_iso_code ||
        COUNTRIES.find((c) => c.dialCode === resolvedCountryCode)?.code ||
        "IN";

    // Event details may sit top-level or inside form_filled / extras / meta_json
    const eventFields = resolveExtLeadEventFields(actionItem);

    const lead: ILead = {
        ...initialLead,
        reference_from_action_item_uuid: actionItem.action_item_uuid,
        name_of_guest: actionItem.name,
        contact_number_one: replaceCountryCode(actionItem.mobile_no),
        country_code: resolvedCountryCode,
        country_iso_code: resolvedCountryIsoCode,
        email: actionItem.email || null,
        remarks: actionItem.remarks || "",
        description: actionItem.description || "",
        followup_date: actionItem.followup_date || null,
        date_of_visit: actionItem.create_ts,
        source: actionItem.source || "Direct",
        handled_by_employee_uuid: actionItem.handled_by_employee_uuid as string,
        handled_by_employee: actionItem.handled_by_employee as string,
        lead_source_uuid: resolvedLeadSourceUuid || actionItem.lead_source_uuid || "",
        priority: actionItem.priority || "HOT",
        additional_contact_number:
            actionItem.additional_contact_number?.map((c) => ({
                contact_number: replaceCountryCode(c.contact_number),
                country_code: c.country_code || "+91",
                country_iso_code:
                    c.country_iso_code ||
                    COUNTRIES.find((cntry) => cntry.dialCode === c.country_code)?.code ||
                    "IN",
                name: c.name || "",
            })) || [],
        multiple_events_list: [
            {
                ...initialMultipleEvent,
                status: driveStatusName,
                drive_status_uuid: driveStatusUuid,
                actual_status: driveActualStatusName,
                event_time_uuid: eventFields.event_time_uuid,
                event_time: eventFields.event_time,
                event_type: eventFields.event_type,
                event_uuid: eventFields.event_uuid,
                other_event_name: eventFields.other_event_name,
                followup_date: actionItem.followup_date as string,
                function_date: eventFields.function_date,
                function_month: actionItem.function_month as string,
                guest_budget: eventFields.guest_budget,
                estimated_rough_revenue: eventFields.estimated_rough_revenue,
                number_of_guests: eventFields.number_of_guests,
                number_of_rooms: eventFields.number_of_rooms,
                preferred_venue_uuid: eventFields.preferred_venue_uuid,
                preferred_venue: eventFields.preferred_venue,
            },
        ],
    };

    const ext_lead: IActionItem = {
        ...actionItem,
        status: externalStatusName,
        actual_status: externalActualStatus,
        drive_status_uuid: externalStatusUuid,
    };

    return { event_lead: lead, ext_lead };
};